import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { setActiveOrgId } from '../lib/supabaseClient'
import { useAuth } from '../hooks/useAuth'
import { resolveActiveOrg, isAllowedFromOrgs } from '../lib/orgAccess'
import UnauthorizedOrg from '../components/UnauthorizedOrg'

const OrganizationContext = createContext(null)

const OVERRIDE_KEY = 'myklubb_org_override'

const DEFAULT_LOGO = '/assets/myklubb-icon.svg'

const DEFAULT_BRANDING = {
  name: 'MYKLUBB',
  shortName: 'MYKLUBB',
  slug: 'myklubb',
  type: 'platform',
  logoUrl: DEFAULT_LOGO,
  colorPrimary: '#0D0D0D',
  colorSecondary: '#B3F000',
  colorBackground: '#F2F2F3',
  config: {}
}

export function OrganizationProvider({ children }) {
  const { user, staff, isPlatformAdmin, loading: authLoading } = useAuth()
  const [org, setOrg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [overrideOrgId, setOverrideOrgId] = useState(() => {
    return sessionStorage.getItem(OVERRIDE_KEY) || null
  })
  const [myOrgs, setMyOrgs] = useState([])
  const [orgsLoading, setOrgsLoading] = useState(true)

  // Membresias del usuario para esta app ('club'): org_id, name, type, rol, es_principal, etc.
  //
  // Depende del ID, no del objeto. Supabase dispara SIGNED_IN al recuperar el foco
  // (volver de otra app o de una notificacion en el telefono) y useAuth recibe un objeto
  // de usuario NUEVO de la misma persona. Con `[user]` este efecto volvia a correr,
  // ponia orgsLoading en true, el gate de App pintaba "Cargando..." y desmontaba TODA la
  // pantalla: el formulario que se estaba llenando se perdia. El dueno lo vio en el
  // celular (2026-09-05).
  const userId = user?.id || null
  useEffect(() => {
    let alive = true
    const load = async () => {
      if (!userId) { setMyOrgs([]); setOrgsLoading(false); return }
      setOrgsLoading(true)
      try {
        const { data, error } = await supabase.rpc('get_my_orgs', { p_app_key: 'club' })
        if (alive) setMyOrgs(error ? [] : data || [])
      } catch {
        // Rechazo de red: sin orgs, pero orgsLoading debe bajar (no colgar el gate).
        if (alive) setMyOrgs([])
      } finally {
        if (alive) setOrgsLoading(false)
      }
    }
    load()
    return () => { alive = false }
  }, [userId])

  // Org activa por membresias (override valido > principal > primera)
  const memberActive = resolveActiveOrg(myOrgs, overrideOrgId)
  // El org_id efectivo: override de platform admin (comportamiento actual, sin
  // restringir a myOrgs) > org activa por membresias > fallback al org del staff
  // (cubre platform admins sin membresias, y evita romper el acceso existente).
  const effectiveOrgId = (isPlatformAdmin && overrideOrgId) || memberActive?.org_id || staff?.org_id

  // Fijar el header x-active-org ANTES de cualquier query (aislamiento server-side).
  // Sincrono en render para que los componentes que consultan al montar ya lo lleven.
  // El RLS valida este org_id contra las membresias; si es null cae a la principal.
  setActiveOrgId(effectiveOrgId || null)

  // Se expone para poder RELEER despues de guardar. Sin esto, el objeto `org` se
  // quedaba con el estado del arranque: la pantalla de configuracion se repuebla
  // desde el, asi que tras guardar mostraba los campos como estaban ANTES -- vacios.
  // Parecia que no se habia guardado, y volver a darle a Guardar mandaba esos vacios
  // de regreso a la base. Es decir: la pantalla borraba lo que acababas de escribir.
  const fetchOrg = useCallback(async () => {
    if (!effectiveOrgId) {
      setOrg(null)
      setLoading(false)
      return null
    }
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', effectiveOrgId)
        .single()

      if (error) throw error
      setOrg(data)
      return data
    } catch (err) {
      console.error('Error fetching organization:', err)
      setOrg(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [effectiveOrgId])

  useEffect(() => { fetchOrg() }, [fetchOrg])

  // Inyectar CSS custom properties para colores dinamicos
  useEffect(() => {
    const root = document.documentElement
    const colors = org || DEFAULT_BRANDING

    root.style.setProperty('--org-primary', colors.color_primary || colors.colorPrimary || '#000000')
    root.style.setProperty('--org-secondary', colors.color_secondary || colors.colorSecondary || '#B5B8BE')
    root.style.setProperty('--org-background', colors.color_background || colors.colorBackground || '#F2F2F3')
  }, [org])

  // Switch de org (solo platform admin)
  const switchOrg = useCallback((orgId) => {
    if (orgId) {
      sessionStorage.setItem(OVERRIDE_KEY, orgId)
    } else {
      sessionStorage.removeItem(OVERRIDE_KEY)
    }
    setOverrideOrgId(orgId)
  }, [])

  // Volver a mi org original
  const resetOrg = useCallback(() => {
    sessionStorage.removeItem(OVERRIDE_KEY)
    setOverrideOrgId(null)
  }, [])

  const value = {
    org,
    loading: loading || orgsLoading,
    myOrgs,
    isAllowed: isAllowedFromOrgs(myOrgs, isPlatformAdmin),
    orgName: org?.name || DEFAULT_BRANDING.name,
    orgShortName: org?.short_name || org?.name || DEFAULT_BRANDING.shortName,
    orgSlug: org?.slug || DEFAULT_BRANDING.slug,
    orgType: org?.type || DEFAULT_BRANDING.type,
    orgLogo: org?.logo_url || DEFAULT_LOGO,
    orgColors: {
      primary: org?.color_primary || DEFAULT_BRANDING.colorPrimary,
      secondary: org?.color_secondary || DEFAULT_BRANDING.colorSecondary,
      background: org?.color_background || DEFAULT_BRANDING.colorBackground,
      iconBackground: org?.color_icon_background || org?.color_primary || DEFAULT_BRANDING.colorPrimary
    },
    orgIconBackground: org?.color_icon_background || org?.color_primary || DEFAULT_BRANDING.colorPrimary,
    orgConfig: org?.config || {},
    defaults: DEFAULT_BRANDING,
    // El rol del usuario EN LA ORG ACTIVA. Es la unica pregunta de rol que tiene sentido
    // en un modelo multi-org: `staff.rol` es un CACHE de la membresia PRINCIPAL (lo copia
    // el trigger `sync_staff_principal_org`), asi que para quien pertenece a dos orgs
    // contesta siempre por una de ellas. La base ya dejo de preguntarle en 20260909.
    //
    // Es null cuando no hay membresia en la org activa -- un platform admin impersonando
    // una org ajena --, y ese caso lo cubre `isImpersonatingOrg` en usePermissions.
    rolEnOrgActiva: memberActive?.rol || null,
    // Platform admin: switch de org
    isOrgOverridden: !!overrideOrgId,
    switchOrg,
    resetOrg,
    refreshOrg: fetchOrg
  }

  if (user && !authLoading && !loading && !orgsLoading && !isAllowedFromOrgs(myOrgs, isPlatformAdmin)) {
    return <UnauthorizedOrg />
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (context === null) {
    return {
      org: null,
      loading: true,
      myOrgs: [],
      isAllowed: false,
      orgName: DEFAULT_BRANDING.name,
      orgShortName: DEFAULT_BRANDING.shortName,
      orgSlug: DEFAULT_BRANDING.slug,
      orgType: DEFAULT_BRANDING.type,
      orgLogo: DEFAULT_LOGO,
      orgColors: {
        primary: DEFAULT_BRANDING.colorPrimary,
        secondary: DEFAULT_BRANDING.colorSecondary,
        background: DEFAULT_BRANDING.colorBackground
      },
      orgConfig: {},
      defaults: DEFAULT_BRANDING,
      isOrgOverridden: false,
      switchOrg: () => {},
      resetOrg: () => {}
    }
  }
  return context
}
